/** A view is read with filters and ordering; a procedure is executed with parameters. */
export type DiagnosticsObjectKind = 'view' | 'procedure';

export type DiagnosticsFilterOp = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le';

export type DiagnosticsSortDirection = 'asc' | 'desc';

/** The app role the staff token of a Producer Admins or MCP Users member carries; the diagnostics API refuses everyone else. */
export const DIAGNOSTICS_ROLE = 'Mcp.User';

export const DIAGNOSTICS_TOP_DEFAULT = 100;
export const DIAGNOSTICS_TOP_MAX = 1000;

export interface DiagnosticsColumnV1 {
  name: string;
  sqlType: string;
}

/** `name` carries no `@`. `default` is the SQL default as text, null when the parameter has none. */
export interface DiagnosticsParameterV1 {
  name: string;
  sqlType: string;
  default: string | null;
  required: boolean;
}

export interface DiagnosticsObjectV1 {
  name: string;
  object: string;
  kind: DiagnosticsObjectKind;
  description: string;
  /** False when the object exists in source but not in this environment's database. */
  deployed: boolean;
  parameters: DiagnosticsParameterV1[];
  /** Populated for deployed views, empty for procedures. */
  columns: DiagnosticsColumnV1[];
}

export interface DiagnosticsDatabaseV1 {
  key: string;
  displayName: string;
  catalog: string;
  /** False when the database could not be queried; `error` says why. */
  reachable: boolean;
  error: string | null;
  objects: DiagnosticsObjectV1[];
}

/** GET `diagnostics/v1/catalog`. */
export interface DiagnosticsCatalogResponseV1 {
  environment: string;
  databases: DiagnosticsDatabaseV1[];
}

export interface DiagnosticsFilterV1 {
  column: string;
  op: DiagnosticsFilterOp;
  /** Always a string on the wire, whatever the column's type. */
  value: string;
}

export interface DiagnosticsOrderByV1 {
  column: string;
  direction: DiagnosticsSortDirection;
}

export type DiagnosticsParameterValue = string | number | boolean;

/**
 * POST `diagnostics/v1/run`. `parameters` applies to procedures (an omitted parameter takes its default);
 * `filters` (AND-combined) and `orderBy` apply to views.
 */
export interface DiagnosticsRunRequestV1 {
  database: string;
  name: string;
  parameters?: Record<string, DiagnosticsParameterValue>;
  filters?: DiagnosticsFilterV1[];
  orderBy?: DiagnosticsOrderByV1;
  top: number;
}

export interface DiagnosticsRunResponseV1 {
  environment: string;
  database: string;
  name: string;
  columns: DiagnosticsColumnV1[];
  rows: Record<string, unknown>[];
  rowCount: number;
  truncated: boolean;
  elapsedMs: number;
}

/** RFC 7807 problem details: the body of every error yt-mcp-server answers, and of the gateway's role refusal. */
export interface ProblemDetailsV1 {
  title?: string;
  status?: number;
  detail?: string;
}
